import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/events/event-soir.png";

const niceJazzFestival: ChauffeurPage = {
  slug: "nice-jazz-festival",
  title: "Chauffeur privé Nice Jazz Festival – VTC & transferts",
  subtitle:
    "Chauffeur privé pendant le Nice Jazz Festival : accès place Masséna et jardins Albert 1er, transferts aéroport et retours de nuit après les concerts.",
  keywords: [
    "chauffeur nice jazz festival",
    "vtc nice jazz festival",
    "chauffeur privé concert nice",
  ],
  hero: {
    title: "Chauffeur privé pendant le Nice Jazz Festival",
    subtitle:
      "Concerts place Masséna et Théâtre de Verdure : un chauffeur dédié quand le centre de Nice se ferme et que le stationnement disparaît.",
    keywords: [
      "chauffeur Nice Jazz Festival",
      "VTC place Masséna",
      "transfert aéroport Nice concert",
    ],
    imageSrc: heroImage.src,
    imageAlt: "Chauffeur privé pendant le Nice Jazz Festival",
    badges: ["Accueil aéroport", "Dépose centre-ville", "Retours de nuit"],
  },
  benefits: {
    heading: "Pourquoi un chauffeur pendant le Nice Jazz Festival",
    items: [
      {
        title: "Centre de Nice sans stationnement",
        description:
          "Place Masséna et jardins Albert 1er en plein cœur de ville : le chauffeur vous dépose au plus près et repart, sans tourner pour se garer.",
      },
      {
        title: "Aéroport à quelques minutes",
        description:
          "Nice Côte d'Azur est à moins de 7 km du festival. Accueil pancarte, suivi de vol et transfert direct vers votre hôtel niçois.",
      },
      {
        title: "Concerts en soirée",
        description:
          "Les scènes jouent tard en juillet : le chauffeur patiente et assure le retour dès la fin du concert, sans file de taxi.",
      },
      {
        title: "5,0/5 sur 38 avis Google",
        description:
          "Ponctualité, discrétion et véhicules soignés : la régularité qui vaut à KS Rent Services une note de 5,0/5 sur 38 avis Google.",
      },
    ],
  },
  services: {
    heading: "Services chauffeur pendant le Nice Jazz Festival",
    services: [
      {
        title: "Accueil aéroport de Nice",
        text: "Transfert aéroport ↔ centre de Nice avec suivi de vol, à quelques minutes des jardins Albert 1er.",
      },
      {
        title: "Dépose au plus près des scènes",
        text: "Approche place Masséna et Théâtre de Verdure selon les rues fermées et l'horaire des concerts.",
      },
      {
        title: "Mise à disposition soirée",
        text: "Chauffeur dédié le temps du dîner et du concert, avec attente sur place.",
      },
      {
        title: "Retours de nuit",
        text: "Fin de concert tardive : retour direct à l'hôtel sur Nice, Cannes ou le long de la Riviera.",
      },
      {
        title: "Navettes groupes & invités",
        text: "Coordination multi-véhicules pour familles, groupes d'amis et invités.",
      },
    ],
  },
  locations: {
    heading: "Points clés pendant le Nice Jazz Festival",
    items: [
      {
        name: "Place Masséna",
        description:
          "Scène principale au cœur de Nice, desservie par le tramway.",
      },
      {
        name: "Jardins Albert 1er / Théâtre de Verdure",
        description: "Scène en plein air entre la Promenade et le centre.",
      },
      {
        name: "Aéroport de Nice Côte d'Azur",
        description: "Terminaux 1 et 2, à moins de 7 km du festival.",
      },
      {
        name: "Promenade des Anglais & hôtels",
        description: "Palaces et hôtels du front de mer, à quelques minutes.",
      },
      {
        name: "Vieux Nice",
        description:
          "Restaurants et bars pour le dîner avant ou après le concert.",
      },
    ],
  },
  generic: {
    heading: "Le Nice Jazz Festival vu d'un chauffeur",
    subheading:
      "Chaque été en juillet, le centre de Nice vibre plusieurs soirs de suite entre la place Masséna et les jardins Albert 1er.",
    paragraphs: [
      "Chaque année en juillet, le Nice Jazz Festival installe ses scènes en plein cœur de ville, place Masséna et au Théâtre de Verdure des jardins Albert 1er. Les rues alentour sont fermées à la circulation et le stationnement devient introuvable : un chauffeur vous dépose au point autorisé le plus proche, sans perdre de temps à chercher une place.",
      "L'aéroport de Nice Côte d'Azur est à moins de sept kilomètres du festival. Avec le suivi de vol et l'accueil pancarte, votre transfert vers l'hôtel se fait sans attente, même en pleine affluence estivale.",
      "Les concerts se terminent tard et les taxis se raréfient à la sortie. La mise à disposition garde le même chauffeur pour le dîner puis le concert, et garantit un retour direct dès la dernière note.",
    ],
    bullets: [
      "Dépose au plus près des scènes",
      "Aéroport à moins de 7 km",
      "Retours après les concerts",
    ],
  },
  process: {
    heading: "Réserver un chauffeur pour le Nice Jazz Festival",
    steps: [
      "Indiquez vos soirs de concert, votre hôtel et vos horaires.",
      "Nous calons les déposes selon les rues fermées autour de la place Masséna.",
      "Le chauffeur se présente en tenue, avec suivi de vol pour vos arrivées à Nice.",
      "Transfert direct ou mise à disposition pour couvrir dîner, concert et retour.",
    ],
  },
  faq: [
    {
      q: "Comment réserver un chauffeur privé pendant le Nice Jazz Festival ?",
      a: "Réservez à l'avance : en juillet, Nice affiche complet et la demande grimpe les soirs de concert. Précisez vos dates, votre hôtel et vos horaires pour que nous bloquions un chauffeur dédié.",
    },
    {
      q: "Où le chauffeur peut-il me déposer près des scènes ?",
      a: "Le festival se tient place Masséna et aux jardins Albert 1er, en zone piétonne et à circulation restreinte. Le chauffeur vous dépose au point autorisé le plus proche, puis vous reprend au même endroit à la fin du concert.",
    },
    {
      q: "Gérez-vous les transferts depuis l'aéroport de Nice ?",
      a: "Oui, avec suivi de vol et accueil pancarte. L'aéroport de Nice Côte d'Azur est à moins de 7 km du centre : le transfert vers votre hôtel est rapide, même en haute saison.",
    },
    {
      q: "Le stationnement est-il vraiment un problème pendant le festival ?",
      a: "Oui. En plein centre de Nice, les parkings saturent et les rues autour des scènes sont fermées les soirs de concert. Le chauffeur vous évite de chercher une place : il dépose, patiente ou revient à l'heure convenue.",
    },
    {
      q: "Assurez-vous les retours tardifs après les concerts ?",
      a: "Oui. Les concerts se terminent tard et les taxis manquent à la sortie : le chauffeur patiente et garantit votre retour direct à l'hôtel, sur Nice ou ailleurs sur la Riviera.",
    },
    {
      q: "Quels véhicules proposez-vous ?",
      a: "Berlines, SUV et vans avec chauffeur selon la taille de votre groupe, pour un trajet confortable entre l'hôtel, le dîner et le festival.",
    },
    {
      q: "Pouvez-vous prendre en charge une famille ou un groupe ?",
      a: "Oui. Nous coordonnons plusieurs véhicules pour les familles et groupes d'amis, avec le même niveau de ponctualité pour tout le monde.",
    },
  ],
  relatedLinks: [
    {
      label: "Transferts avec chauffeur à Nice",
      href: "/chauffeur/nice",
    },
    {
      label: "Navette aéroport de Nice",
      href: "/chauffeur/aeroport",
    },
    {
      label: "VTC Côte d’Azur",
      href: "/chauffeur/cote-d-azur",
    },
    {
      label: "Location de voiture Nice",
      href: "/location/location-voiture-nice",
    },
  ],
  cta: {
    title: "Votre chauffeur pour le Nice Jazz Festival",
    text: "Accueil aéroport, dépose au plus près des scènes et retours de nuit : réservez un chauffeur dédié pour vos soirées de concert.",
    primaryHref: "/reservation",
    primaryLabel: "Réserver un chauffeur",
  },
  seo: {
    title: "Chauffeur Nice Jazz Festival – VTC privé | KS Rent",
    description:
      "Chauffeur privé pendant le Nice Jazz Festival : transfert aéroport de Nice, dépose place Masséna et jardins Albert 1er, retours de nuit après les concerts.",
    canonical: "/chauffeur/nice-jazz-festival",
  },
};

export default niceJazzFestival;
